import {
  ButtonDirective,
  DesktopOS,
  DeviceDetectorService,
  formatShortcut,
  hasPrimaryModifier,
  isEditableTarget,
} from '@imagise/shared';
import { Component, Input, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ImageCommandsService } from '../../../command/image-commands.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { EditMode, EditModeMenuItem, getEditModeMenuItem } from '@imagise/entities';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessagingService } from '../../infrastructure/messaging/messaging.service';
import { RefreshEditOptionsMessage } from '../../domain/models/messages';
import { EditorFormStore } from '../../domain';
import { EditModeHelpDialogComponent } from './edit-mode-help-dialog.component';

/** One editor fab button: everything the template needs to render it. */
interface EditorFabButton {
  key: string;
  ariaLabel: () => string;
  icon: () => string;
  tooltip: () => string;
  disabled: () => boolean;
  onClick: () => void;
  visible?: () => boolean;
}

/** A visual group of fab buttons; the class places it on the panel grid. */
interface EditorFabGroup {
  class: string;
  buttons: EditorFabButton[];
}

@Component({
  selector: 'lib-top-panel',
  templateUrl: './top-panel.component.html',
  styleUrls: ['./top-panel.component.scss'],
  imports: [MatTooltipModule, MatIconModule, TranslateModule, ButtonDirective],
  host: { '(document:keydown)': 'handleKeydown($event)' },
})
export class TopPanelComponent {
  private readonly commandSvc = inject(ImageCommandsService);
  private readonly messageBus = inject(MessagingService);
  private readonly editorStore = inject(EditorFormStore);

  private readonly dialog = inject(MatDialog);
  private translationService = inject(TranslateService);
  private readonly deviceDetector = inject(DeviceDetectorService);

  private pageCount$!: number;

  readonly editMode = input<EditMode>(EditMode.None);

  readonly animationPreviewAvailable = input(false);

  readonly animationPreviewPlaying = input(false);

  @Input() set pageCount(value: number) {
    this.pageCount$ = value;
    if (value > 0) {
      this.commandSvc.currentPageIndex = 0;
    }
  }
  get pageCount(): number {
    return this.pageCount$;
  }


  readonly download = output();

  readonly animationPreviewToggled = output<void>();

  protected readonly fabGroups: EditorFabGroup[] = [
    {
      class: 'tool-actions-panel',
      buttons: [
        {
          key: 'reset',
          ariaLabel: () => 'Revert',
          icon: () => 'reset',
          tooltip: () => this.resetTooltip,
          disabled: () => !this.canReset,
          onClick: () => this.reset(),
        },
        {
          key: 'help',
          ariaLabel: () => this.helpTooltip,
          icon: () => 'question_circle',
          tooltip: () => this.helpTooltip,
          disabled: () => !this.canOpenModeHelp,
          onClick: () => this.openModeHelp(),
        },
        {
          key: 'animation-preview',
          ariaLabel: () => this.animationPreviewTooltip,
          icon: () => this.animationPreviewIcon,
          tooltip: () => this.animationPreviewTooltip,
          disabled: () => false,
          onClick: () => this.toggleAnimationPreview(),
          visible: () => this.animationPreviewAvailable(),
        },
      ],
    },
    {
      class: 'undo-redo-panel',
      buttons: [
        {
          key: 'undo',
          ariaLabel: () => 'Undo',
          icon: () => 'undo',
          tooltip: () => this.undoTooltip,
          disabled: () => !this.canUndo,
          onClick: () => this.undo(),
        },
        {
          key: 'redo',
          ariaLabel: () => 'Redo',
          icon: () => 'redo',
          tooltip: () => this.redoTooltip,
          disabled: () => !this.canRedo,
          onClick: () => this.redo(),
        },
      ],
    },
  ];

  get canUndo(): boolean {
    return this.commandSvc.canUndo();
  }

  get canRedo(): boolean {
    return this.commandSvc.canRedo();
  }

  get canReset(): boolean {
    return this.commandSvc.size() > 0;
  }

  get activeModeInfo(): EditModeMenuItem | null {
    return getEditModeMenuItem(this.editMode());
  }

  get canOpenModeHelp(): boolean {
    return this.activeModeInfo !== null;
  }

  get undoTooltip(): string {
    const shortcut = formatShortcut('Mod+Z', this.deviceDetector.isApplePlatform());
    return `${this.translationService.instant('EDITOR.TOP_PANEL.UNDO')} (${shortcut})`;
  }

  get redoTooltip(): string {
    return `${this.translationService.instant('EDITOR.TOP_PANEL.REDO')} (${this.redoShortcut})`;
  }

  /**
   * The conventional redo combo differs per OS: ⇧⌘Z on Apple platforms,
   * Ctrl+Shift+Z on Linux/Unix, Ctrl+Y on Windows. The handler accepts all
   * of them everywhere — the tooltip advertises the local convention.
   */
  private get redoShortcut(): string {
    if (this.deviceDetector.isApplePlatform()) {
      return formatShortcut('Shift+Mod+Z', true);
    }

    const desktopOS = this.deviceDetector.desktopOS();
    const prefersShiftZ = desktopOS === DesktopOS.Linux || desktopOS === DesktopOS.Unix;
    return formatShortcut(prefersShiftZ ? 'Mod+Shift+Z' : 'Mod+Y', false);
  }

  get resetTooltip(): string {
    return this.translationService.instant('EDITOR.TOP_PANEL.REVERT');
  }

  get helpTooltip(): string {
    const modeInfo = this.activeModeInfo;
    if (!modeInfo) {
      return this.translationService.instant('EDITOR.TOP_PANEL.HELP.TOOLTIP_DISABLED');
    }

    return this.translationService.instant('EDITOR.TOP_PANEL.HELP.TOOLTIP', {
      tool: this.translationService.instant(modeInfo.titleKey),
    });
  }

  get animationPreviewTooltip(): string {
    return this.translationService.instant(
      this.animationPreviewPlaying()
        ? 'EDITOR.TOP_PANEL.PAUSE_ANIMATION'
        : 'EDITOR.TOP_PANEL.PLAY_ANIMATION',
    );
  }

  get animationPreviewIcon(): string {
    return this.animationPreviewPlaying() ? 'pause' : 'play';
  }

  /** Download makes sense only when the user actually changed something. */
  get canDownload(): boolean {
    return this.editorStore.form.controls.ui.controls.dirty.getRawValue();
  }

  get downloadTooltip(): string {
    return this.translationService.instant(
      this.canDownload ? 'EDITOR.TOP_PANEL.DOWNLOAD' : 'EDITOR.TOP_PANEL.DOWNLOAD_DISABLED'
    );
  }

  /**
   * Global editor shortcuts: Ctrl/⌘+Z undoes, Ctrl+Y or ⇧⌘Z redoes. Yields
   * to open dialogs and editable fields, where the same keys mean native
   * text-editing undo/redo.
   */
  protected handleKeydown(event: KeyboardEvent): void {
    if (!hasPrimaryModifier(event, this.deviceDetector.isApplePlatform()) || event.altKey) {
      return;
    }

    if (this.dialog.openDialogs.length > 0 || isEditableTarget(event.target)) {
      return;
    }

    const key = event.key.toLowerCase();
    const isUndoCombo = key === 'z' && !event.shiftKey;
    const isRedoCombo = (key === 'z' && event.shiftKey) || (key === 'y' && !event.shiftKey);

    if (isUndoCombo && this.canUndo) {
      event.preventDefault();
      this.undo();
    } else if (isRedoCombo && this.canRedo) {
      event.preventDefault();
      this.redo();
    }
  }

  undo(): void {
    this.editorStore.clearDraft();
    this.commandSvc.undo();
    this.messageBus.publish(new RefreshEditOptionsMessage());
  }

  redo(): void {
    this.editorStore.clearDraft();
    this.commandSvc.redo();
    this.messageBus.publish(new RefreshEditOptionsMessage());
  }

  reset(): void {
    this.editorStore.clearDraft();
    this.commandSvc.reset();
    this.messageBus.publish(new RefreshEditOptionsMessage());
  }

  openModeHelp(): void {
    const modeInfo = this.activeModeInfo;
    if (!modeInfo) {
      return;
    }

    EditModeHelpDialogComponent.open(this.dialog, modeInfo);
  }

  toggleAnimationPreview(): void {
    this.animationPreviewToggled.emit();
  }

}
